import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { AnimatePresence, motion } from 'framer-motion';

import { Box, Flex, SimpleGrid } from '@chakra-ui/react';
import { headerHeight } from '@udacity/udacity-header-v2';

import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { getFormalDegreeKey } from '~/features/analytics/utils/get-formal-degree-key';
import { CatalogSearchResults } from '~/features/catalog/models/catalog-search-result';
import { UriFilter } from '~/features/catalog/models/filter';
import { updateCatalogSearchValue, updateCatalogUrlState } from '~/features/catalog/utils/catalog-url-state-utils';
import { CatalogCard, CatalogCardSkeleton } from '~/features/experiments/ab_plp_redesign_2025/components/catalog-card';
import { CatalogFilters } from '~/features/experiments/ab_plp_redesign_2025/components/catalog-filters';
import { CatalogNoResults } from '~/features/experiments/ab_plp_redesign_2025/components/catalog-no-results';
import { PaginationWithLinks } from '~/features/experiments/ab_plp_redesign_2025/components/pagination-with-links';
import { CatalogStructuredData } from '~/features/seo/components/catalog-structured-data';

import { INLINE_SEARCH_ORIGIN } from '../constants';
import { useCatalogSearchResults } from '../hooks/use-catalog-search-results';
import { setCatalogSearchDraft } from '../utils/catalog-search-draft';

import { CatalogSearchHeader } from './catalog-search-header';
import { CatalogSearchNoResults } from './catalog-search-no-results';
import { CatalogSearchResultsToolbar } from './catalog-search-results-toolbar';

const ENROLLMENT_SCOPES = ['enrolled', 'completed'];

export function CatalogSearchCatalog({
  initialCatalogSearchResults,
  schoolUriFilters,
  skillUriFilters,
}: {
  initialCatalogSearchResults: CatalogSearchResults | null;
  schoolUriFilters: UriFilter[];
  skillUriFilters: UriFilter[];
}) {
  const router = useRouter();
  const {
    catalogSearchResults,
    isFetchingCatalogSearchResults,
    isLoading,
    numberOfSelectedFilters,
    page,
    searchValue,
    selectedFilters,
    totalPages,
  } = useCatalogSearchResults({ initialCatalogSearchResults, schoolUriFilters, skillUriFilters });

  const handlePageChange = useCallback(
    (page: number) => {
      updateCatalogUrlState({
        router,
        updatedRouteParameters: {
          page: page === 0 ? undefined : `page-${page + 1}`,
        },
      });
    },
    [router]
  );

  const handleClearSearch = useCallback(() => {
    setCatalogSearchDraft('', INLINE_SEARCH_ORIGIN);
    updateCatalogSearchValue({ router, searchValue: '' });
  }, [router]);

  const searchResultItems = catalogSearchResults?.searchResultItems;
  const trimmedSearchValue = searchValue?.trim();

  return (
    <>
      <ContentContainer contentMaxWidth='content-max-width-lg' isolation='isolate'>
        <Flex flexDirection='column' gap={{ base: '16px', lg: '24px' }} position='relative'>
          <CatalogSearchHeader searchValue={searchValue} selectedFilters={selectedFilters} />
          <Flex alignItems='flex-start' as='section' flexDirection={{ base: 'column', lg: 'row' }} gap={6}>
            <Box
              display={{ base: 'none', lg: 'block' }}
              flexShrink={0}
              maxHeight='calc(100vh - 164px)'
              overflowY='auto'
              position='sticky'
              top={headerHeight}
              width='256px'
              sx={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#DFDFDF transparent',
              }}
            >
              <CatalogFilters
                isLoading={isFetchingCatalogSearchResults}
                numberOfResults={catalogSearchResults?.numberOfHits}
                numberOfSelectedFilters={numberOfSelectedFilters}
                selectedFilters={selectedFilters}
                skillUriFilters={skillUriFilters}
              />
            </Box>
            <Flex flex={1} flexDirection='column' gap='12px' width='100%'>
              <Box bgColor='white' paddingY='12px' position='sticky' top={headerHeight} zIndex={5}>
                <CatalogSearchResultsToolbar
                  isLoading={isLoading}
                  numberOfResults={catalogSearchResults?.numberOfHits}
                  numberOfSelectedFilters={numberOfSelectedFilters}
                  selectedFilters={selectedFilters}
                  skillUriFilters={skillUriFilters}
                />
              </Box>
              <AnimatePresence initial={false} mode='popLayout'>
                {!isLoading && Boolean(searchResultItems?.length) && (
                  <Box
                    key='catalog-cards'
                    animate={{ opacity: 1 }}
                    as={motion.div}
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                  >
                    <SimpleGrid columns={{ base: 2, md: 3 }} spacing={{ base: '8px', lg: '24px' }}>
                      {searchResultItems?.map((searchResultItem) => (
                        <CatalogCard
                          key={searchResultItem.key}
                          catalogItem={searchResultItem}
                          width='100%'
                          priority
                          onClick={() => {
                            const formalDegreeKey = getFormalDegreeKey(searchResultItem);
                            trackEvent({
                              type: 'click',
                              name: 'catalogCtaClicked',
                              label: searchResultItem.title!,
                              action: 'program_card_clicked',
                              ...(formalDegreeKey && {
                                additionalAttributes: {
                                  formal_degree_key: formalDegreeKey,
                                },
                              }),
                            });
                          }}
                        />
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
                {isLoading && (
                  <Box
                    key='catalog-card-skeletons'
                    animate={{ opacity: 1 }}
                    as={motion.div}
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                  >
                    <SimpleGrid columns={{ base: 2, md: 3 }} flex={1} spacing={{ base: '8px', lg: '24px' }}>
                      {[...Array(24)].map((_, i) => (
                        <CatalogCardSkeleton
                          key={i}
                          type={ENROLLMENT_SCOPES.includes(selectedFilters.scope?.[0] ?? '') ? 'enrollment' : 'catalog'}
                          width='100%'
                        />
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
                {!isLoading && !searchResultItems?.length && (
                  <Box
                    key='catalog-no-results'
                    animate={{ opacity: 1 }}
                    as={motion.div}
                    exit={{ opacity: 0 }}
                    flex={1}
                    initial={{ opacity: 0 }}
                    width='100%'
                  >
                    {trimmedSearchValue ? (
                      <CatalogSearchNoResults searchValue={trimmedSearchValue} onClearSearch={handleClearSearch} />
                    ) : (
                      <CatalogNoResults />
                    )}
                  </Box>
                )}
              </AnimatePresence>
              <Flex justifyContent='center' mt='28px'>
                <PaginationWithLinks
                  currentPage={page}
                  totalPages={totalPages}
                  pageIsZeroIndexed
                  onChange={handlePageChange}
                />
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </ContentContainer>
      <CatalogStructuredData catalogSearchResults={catalogSearchResults} />
    </>
  );
}
